import React, { useEffect, useState, useContext } from "react";
import Loader from "../../common/components/Loader/Loader";
import AFErrorDialog from "../components/AFErrorDialog";
import { SplitTreatments } from "@splitsoftware/splitio-react";
import { FeatureFlag } from "../../compare/enums/FeatureFlags";
import { useHistory } from "react-router-dom";
import {
    getUserList,
    startApplication,
    resumeApplication
} from "../api/ApiAppForm";
import { AppFormControllerContext } from "../providers/AppFormProvider";
import { getInsurerName } from "../shared/AppFormHelper";
import useErrorLogger from "../../common/hooks/useErrorLogger";
import { PageType } from "../enums/PageType";
import { IApplicantInfo } from "../interfaces/IAppFormData";
import * as Sentry from "@sentry/react";

const AFContactInfoPage: React.FC = ({ children }) => {
    const history = useHistory();
    const [isInfoReady, setIsInfoReady] = useState(false);
    const [isInErrorState, setIsInErrorState] = useState(true);

    const { actions, selectors } = useContext(AppFormControllerContext);

    // Initiated application parameters
    const search = new URLSearchParams(location.search);
    const providerId = search.has("providerId") && parseInt(search.get("providerId"));
    const groupId = search.has("groupId") && search.get("groupId");
    const caseProduct = search.has("caseProduct") && search.get("caseProduct");

    // Resumed application parameters
    const clientId : number = search.has("client_id") && parseInt(search.get("client_id"));
    const uId = search.has("uid") && search.get("uid");
    const collectionId = search.has("collection_id") && parseInt(search.get("collection_id"));

    const clearLocalStorage = (): void => {
        actions.reset();
    };

    const handleCaseProducts = () => {
        const cases = caseProduct?.split(",");
        const casesList = cases.map((item: string) => {
            const caseItem = item.split(":");
            return {
                caseID: (caseItem?.length && caseItem[0]) || "",
                productID: (caseItem?.length && caseItem[1]) || ""
            };
        });
        actions.setCaseProducts(casesList);
    };

    const getRedirectionUrl = (pageType: PageType, applicationId: number, applicantToken: string, applicantRef: string): string => {
        switch (pageType) {
            case PageType.Setup:
                return `/app-form/setup/info/${applicantRef}`;
            case PageType.AppForm:
                return `/app-form/details/${applicantToken}`;
            case PageType.Declarations:
                return `/app-form/details/${applicantToken}/declarations`;
            case PageType.PolicyOwnership:
                return `/app-form/submit/${applicationId}/ownership`;
            case PageType.Payment:
                return `/app-form/submit/${applicationId}/payment`;
            case PageType.Address:
                return `/app-form/submit/${applicationId}/${applicantRef}/address`;
            case PageType.Consent:
                return `/app-form/submit/${applicationId}/consent/disclosure`;
            case PageType.Signature:
                return `/app-form/submit/${applicationId}/${applicantRef}/signature`;
            case PageType.Submit:
                return `/app-form/submit/${applicationId}/ready`;
            case PageType.Done:
                return `/app-form/done/${applicationId}`;
        }

        return null;
    };


    const handleGetInfo = (): void => {
        clearLocalStorage();
        setIsInfoReady(false);

        if (clientId && collectionId) {
            // Existing application
            resumeApplication({ clientId: clientId, uid: uId, collectionId: collectionId })
                .then((res) => {
                    var applicantRef = "applicant" + res.currentClientNumber;
                    var linkTarget: string = getRedirectionUrl(res.pageType, res.applicationId, res.currentApplicantToken, applicantRef);

                    if (linkTarget) {
                        // Set context for the page being loaded, so that validation passes
                        actions.setQuoteGroupId(res.groupId);
                        actions.setInsurer({
                            id: res.provider,
                            name: res.providerName
                        });
                        actions.setApplicationId(res.applicationId);
                        actions.setApplicationToken(res.applicationToken);
                        const mappedApplicantInfo: IApplicantInfo[] = res.applicants.map((a, index) => ({
                            ...a,
                            personId: a.applicantId,
                            params: `applicant${index + 1}`,
                            label: `Applicant ${index + 1}`,
                            isCompleted: res.pageType == PageType.Done
                        }));                      
                        actions.setApplicantList(mappedApplicantInfo);
                        history.push(linkTarget);
                        setIsInfoReady(true);
                    }
                })
                .catch(() => {
                    useErrorLogger();
                    setIsInErrorState(true);
                    setIsInfoReady(true);
                    if (typeof Sentry !== 'undefined') {
                        Sentry.captureMessage(`Error occured on the AppFormInit Page. Failed to resume application for collectionId: ${collectionId}`, "error");
                    }
                });
        } else {
            // New application
            getUserList(groupId)
                .then((res) => {
                    if (res?.applicants && res?.applicants?.length) {
                        actions.setQuoteGroupId(groupId);
                        actions.setInsurer({
                            id: providerId,
                            name: getInsurerName(providerId)
                        });

                        handleCaseProducts();
                        actions.setDASessionUserId(res?.digitalAdviceSessionUserID);
                        startApplication(groupId, providerId, res?.digitalAdviceSessionUserID, res?.applicants)
                            .then((res) => {
                                actions.setApplicationId(res.applicationId);
	                            actions.setApplicationToken(res.applicationToken);
                                actions.setApplicantList(res?.applicants);

                                var linkTarget: string = getRedirectionUrl(res.pageType, res.applicationId, res.currentApplicantToken, "applicant1");
                                history.push(linkTarget);
                                setIsInfoReady(true);
                            })
                            .catch(() => {
                                useErrorLogger();
                                setIsInErrorState(true);
                                setIsInfoReady(true);
                                if (typeof Sentry !== 'undefined') {
                                    Sentry.captureMessage(`Error occured on the AppFormInit Page. Failed to start application for groupId: ${groupId}`);
                                }
                            });
                    }
                })
                .catch(() => {
                    useErrorLogger();
                    setIsInErrorState(true);
                    if (typeof Sentry !== 'undefined') {
                        Sentry.captureMessage(`Error occured on the AppFormInit Page. Failed to getUserList for groupId: ${groupId}`, "error");
                    }
                });
        }
    };

    useEffect(() => {
        handleGetInfo();
    }, []);



    return (
        <SplitTreatments names={Object.values(FeatureFlag)}>
            {({ isReady }) => {
                /* Pre-load all the treatments */
                if ((window.splitioFactory && !isReady) || !isInfoReady)
                    return <Loader size="full-page" />;
                return <React.Fragment>
                    {isInErrorState && <AFErrorDialog />}
                    {children}
                </React.Fragment>;
            }}
        </SplitTreatments>
    );
};

export default AFContactInfoPage;
